package com.cs.ejobin.ui.screens.auth

import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import com.cs.ejobin.i18n.Keys
import com.cs.ejobin.i18n.t
import com.cs.ejobin.model.SkillCatalog
import com.cs.ejobin.platform.openUrlDocument
import com.cs.ejobin.ui.components.ErrorText
import com.cs.ejobin.ui.components.ScreenScaffold
import com.cs.ejobin.ui.components.SkillCatalogChips
import com.cs.ejobin.ui.theme.AppColors
import com.cs.ejobin.ui.theme.AppSurfaces
import com.cs.ejobin.vm.SignupViewModel
import ejobin.composeapp.generated.resources.Res
import ejobin.composeapp.generated.resources.eJobin_logo
import org.jetbrains.compose.resources.painterResource

private data class LegalDoc(
    val titleKey: String,
    val url: String
)

@Composable
fun SignupScreen(
    vm: SignupViewModel,
    onBackToLogin: () -> Unit,
    onSignedUpGoHome: () -> Unit, // ✅ renommé
    onNeedEmailConfirmation: () -> Unit
) {
    val state by vm.state.collectAsState()

    var passwordVisible by remember { mutableStateOf(false) }
    var confirmVisible by remember { mutableStateOf(false) }

    val docs = remember {
        listOf(
            LegalDoc(Keys.doc_terms_title, "/legal/01_SOP_Gestion_Incidents_eJobin.pdf"),
            LegalDoc(Keys.doc_privacy_title, "/legal/02_Politique_Anti_NoShow_Annulation_eJobin.pdf"),
            LegalDoc(Keys.doc_cookies_title, "/legal/03_Politique_Paiement_Flux_Financiers_eJobin.pdf"),
            LegalDoc(Keys.doc_subscription_title, "/legal/04_Charte_Engagements_Reciproques_eJobin.pdf"),
            LegalDoc(Keys.doc_legal_notice_title, "/legal/05_Politique_Preuve_Conservation_Donnees_eJobin.pdf"),
        )
    }

    val primaryTextButtonColors = ButtonDefaults.textButtonColors(
        contentColor = AppColors.Primary
    )

    ScreenScaffold(
        title = "",
        scrollable = true,
        topActions = {}
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Spacer(Modifier.height(18.dp))

            Image(
                painter = painterResource(Res.drawable.eJobin_logo),
                contentDescription = "EJobin",
                modifier = Modifier.size(120.dp)
            )

            Spacer(Modifier.height(18.dp))

            Card(
                modifier = Modifier
                    .fillMaxWidth()
                    .widthIn(max = 520.dp),
                colors = AppSurfaces.glassCard(),
                border = CardDefaults.outlinedCardBorder()
            ) {
                Column(
                    modifier = Modifier.padding(18.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = t(Keys.signup_title),
                        style = MaterialTheme.typography.titleLarge,
                        color = AppColors.TextPrimary
                    )

                    Text(
                        text = t(Keys.signup_subtitle),
                        style = MaterialTheme.typography.bodyMedium,
                        color = AppColors.TextPrimary
                    )

                    Spacer(Modifier.height(6.dp))

                    OutlinedTextField(
                        value = state.name,
                        onValueChange = vm::onName,
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        enabled = !state.isLoading,
                        label = { Text(t(Keys.name)) },
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Text,
                            imeAction = ImeAction.Next
                        )
                    )

                    OutlinedTextField(
                        value = state.email,
                        onValueChange = vm::onEmail,
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        enabled = !state.isLoading,
                        label = { Text(t(Keys.email)) },
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Email,
                            imeAction = ImeAction.Next
                        )
                    )

                    OutlinedTextField(
                        value = state.phone,
                        onValueChange = vm::onPhone,
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        enabled = !state.isLoading,
                        label = { Text(t(Keys.phone)) },
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Phone,
                            imeAction = ImeAction.Next
                        )
                    )

                    OutlinedTextField(
                        value = state.password,
                        onValueChange = vm::onPassword,
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        enabled = !state.isLoading,
                        label = { Text(t(Keys.password)) },
                        visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                        trailingIcon = {
                            TextButton(
                                onClick = { passwordVisible = !passwordVisible },
                                enabled = !state.isLoading,
                                colors = primaryTextButtonColors
                            ) { Text(if (passwordVisible) t(Keys.hide) else t(Keys.show)) }
                        },
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Password,
                            imeAction = ImeAction.Next
                        )
                    )

                    OutlinedTextField(
                        value = state.confirmPassword,
                        onValueChange = vm::onConfirmPassword,
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        enabled = !state.isLoading,
                        label = { Text(t(Keys.password_confirm)) },
                        visualTransformation = if (confirmVisible) VisualTransformation.None else PasswordVisualTransformation(),
                        trailingIcon = {
                            TextButton(
                                onClick = { confirmVisible = !confirmVisible },
                                enabled = !state.isLoading,
                                colors = primaryTextButtonColors
                            ) { Text(if (confirmVisible) t(Keys.hide) else t(Keys.show)) }
                        },
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Password,
                            imeAction = ImeAction.Done
                        ),
                        keyboardActions = KeyboardActions(
                            onDone = {
                                if (!state.isLoading) {
                                    vm.signup(
                                        onSuccess = onSignedUpGoHome, // ✅
                                        onNeedEmailConfirmation = onNeedEmailConfirmation
                                    )
                                }
                            }
                        )
                    )

                    OutlinedTextField(
                        value = state.description,
                        onValueChange = vm::onDescription,
                        modifier = Modifier.fillMaxWidth(),
                        enabled = !state.isLoading,
                        label = { Text(t(Keys.description)) },
                        placeholder = { Text(t(Keys.description_placeholder)) },
                        minLines = 3,
                        maxLines = 6
                    )

                    SkillCatalogChips(
                        title = t(Keys.skills_jobs),
                        all = SkillCatalog.entries,
                        selected = state.selectedSkills,
                        onToggle = vm::toggleSkill,
                        enabled = !state.isLoading,
                        columns = 3,
                        collapsedCount = 9
                    )

                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = AppSurfaces.glassCard(),
                        border = CardDefaults.outlinedCardBorder()
                    ) {
                        Column(
                            modifier = Modifier.padding(12.dp),
                            verticalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            Text(
                                text = t(Keys.legal_title),
                                style = MaterialTheme.typography.titleSmall,
                                color = AppColors.TextPrimary
                            )

                            docs.forEach { doc ->
                                Text(
                                    text = "• ${t(doc.titleKey)}",
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable(enabled = !state.isLoading) { openUrlDocument(doc.url) },
                                    color = AppColors.Primary,
                                    style = MaterialTheme.typography.bodyMedium
                                )
                            }

                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Checkbox(
                                    checked = state.acceptedLegal,
                                    onCheckedChange = { if (!state.isLoading) vm.toggleAcceptedLegal() },
                                    enabled = !state.isLoading,
                                    colors = CheckboxDefaults.colors(checkedColor = AppColors.Primary)
                                )

                                Spacer(Modifier.width(8.dp))

                                Text(
                                    text = t(Keys.accept_label),
                                    modifier = Modifier.weight(1f),
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = AppColors.TextPrimary
                                )
                            }
                        }
                    }

                    ErrorText(state.errorKey)

                    Button(
                        onClick = {
                            vm.signup(
                                onSuccess = onSignedUpGoHome, // ✅
                                onNeedEmailConfirmation = onNeedEmailConfirmation
                            )
                        },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = !state.isLoading && state.acceptedLegal,
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Primary)
                    ) {
                        if (state.isLoading) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(18.dp),
                                strokeWidth = 2.dp,
                                color = MaterialTheme.colorScheme.onPrimary
                            )
                            Spacer(Modifier.width(10.dp))
                            Text(t(Keys.loading))
                        } else {
                            Text(t(Keys.create_account))
                        }
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.Center
                    ) {
                        TextButton(
                            onClick = onBackToLogin,
                            enabled = !state.isLoading,
                            colors = primaryTextButtonColors
                        ) {
                            Text(t(Keys.already_have_account))
                        }
                    }
                }
            }

            Spacer(Modifier.height(18.dp))
        }
    }
}